import { z } from "zod";
// ---------- Ingest ----------
export const ContentTypeSchema = z.enum(["text", "file", "link", "note", "audio", "image"]);
export const IngestRequestSchema = z.object({
    title: z.string().nullable().optional(),
    content: z.string().min(1),
    content_type: ContentTypeSchema.default("text"),
    source_url: z.url().nullable().optional(),
    user_tags: z.array(z.string()).default([]),
});
export const IngestResponseSchema = z.object({
    document_id: z.uuid(),
    chunks_count: z.number().int().nonnegative(),
    duplicate: z.boolean().default(false),
    auto_tags: z.array(z.string()).default([]),
    user_tags: z.array(z.string()).default([]),
    room_id: z.uuid().nullable().optional(),
    status: z.enum(["ready", "processing", "duplicate"]).default("ready"),
});
// ---------- Documents ----------
export const DocumentSummarySchema = z.object({
    id: z.uuid(),
    title: z.string().nullable(),
    content_type: ContentTypeSchema,
    source_url: z.string().nullable(),
    summary: z.string().nullable(),
    content_hint: z.string().nullable().optional().default(null),
    color: z.string().nullable().optional().default(null),
    background_image: z.string().nullable().optional().default(null),
    auto_tags: z.array(z.string()),
    user_tags: z.array(z.string()),
    chunk_count: z.number().int(),
    room_id: z.uuid().nullable().optional(),
    is_pinned: z.boolean().default(false),
    pinned_at: z.string().nullable().optional().default(null),
    created_at: z.string(),
    updated_at: z.string(),
});
export const DocumentPageSchema = z.object({
    data: z.array(DocumentSummarySchema),
    next_cursor: z.string().nullable(),
});
export const DocumentStatsSchema = z.object({
    total: z.number().int(),
    this_week: z.number().int(),
    last_captured: z.string().nullable(),
});
export const DocumentDetailSchema = DocumentSummarySchema.extend({
    content: z.string().nullable(),
});
export const DocumentPatchSchema = z.object({
    title: z.string().nullable().optional(),
    user_tags: z.array(z.string()).optional(),
    auto_tags: z.array(z.string()).optional(),
    content: z.string().nullable().optional(),
    room_id: z.uuid().nullable().optional(),
    color: z.string().nullable().optional(),
    background_image: z.string().nullable().optional(),
    is_pinned: z.boolean().optional(),
});
// ---------- Notes ----------
export const NoteSchema = z.object({
    id: z.uuid(),
    title: z.string().nullable().optional().default(null),
    content: z.string(),
    color: z.string().nullable().optional().default(null),
    background_image: z.string().nullable().optional().default(null),
    user_tags: z.array(z.string()),
    is_pinned: z.boolean().default(false),
    pinned_at: z.string().nullable().optional().default(null),
    sort_order: z.number().default(0),
    created_at: z.string(),
    updated_at: z.string(),
});
export const NotePageSchema = z.object({
    data: z.array(NoteSchema),
    next_cursor: z.string().nullable(),
});
// ---------- Query ----------
export const QueryRequestSchema = z.object({
    question: z.string().min(1),
    conversation_id: z.uuid().nullable().optional(),
    top_k: z.number().int().min(1).max(50).default(10),
    search_mode: z.enum(["auto", "palace", "web"]).default("auto"),
});
export const ChunkResultSchema = z.object({
    chunk_id: z.uuid(),
    document_id: z.uuid(),
    title: z.string().nullable(),
    content_type: z.string(),
    source_url: z.string().nullable(),
    content: z.string(),
    created_at: z.string().nullable().optional(),
    score: z.number(),
});
export const WebResultSchema = z.object({
    title: z.string().nullable(),
    url: z.string(),
    score: z.number(),
});
export const QueryResponseSchema = z.object({
    answer: z.string(),
    conversation_id: z.uuid(),
    chunks_used: z.array(ChunkResultSchema),
    web_searched: z.boolean().default(false),
    web_results: z.array(WebResultSchema).default([]),
});
// ---------- Conversation / Message (read models) ----------
export const MessageRoleSchema = z.enum(["system", "user", "assistant"]);
export const MessageSchema = z.object({
    id: z.uuid(),
    conversation_id: z.uuid().optional(),
    role: MessageRoleSchema,
    content: z.string(),
    tool_results: z.record(z.string(), z.unknown()).nullable().optional(),
    created_at: z.string(),
});
export const ConversationSummarySchema = z.object({
    id: z.uuid(),
    title: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    message_count: z.number().int(),
});
export const ConversationDetailSchema = z.object({
    id: z.uuid(),
    title: z.string().nullable(),
    messages: z.array(MessageSchema),
});
// ---------- Rooms ----------
export const RoomSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string().nullable(),
    cover_image: z.string().nullable(),
    icon: z.string().nullable(),
    doc_count: z.number().int().default(0),
    created_at: z.string(),
});
export const RoomCreateSchema = z.object({
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    cover_image: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
});
export const RoomPatchSchema = z.object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    cover_image: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
});
export const RoomOverviewDocSchema = z.object({
    id: z.uuid(),
    title: z.string().nullable(),
    content_type: ContentTypeSchema,
    summary: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const RoomOverviewSchema = RoomSchema.extend({
    recent_docs: z.array(RoomOverviewDocSchema),
});
// ---------- Graph (Cluster View) ----------
export const TagNodeSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    doc_count: z.number().int(),
    x: z.number().nullable().optional(),
    y: z.number().nullable().optional(),
});
export const DocNodeSchema = z.object({
    id: z.uuid(),
    title: z.string().nullable(),
    content_type: ContentTypeSchema,
    created_at: z.string(),
});
export const GraphEdgeSchema = z.object({
    doc_id: z.uuid(),
    tag_id: z.uuid(),
    source: z.enum(["auto", "user"]),
});
export const GraphResponseSchema = z.object({
    tag_nodes: z.array(TagNodeSchema),
    doc_nodes: z.array(DocNodeSchema),
    edges: z.array(GraphEdgeSchema),
});
// ---------- User ----------
export const UserTierSchema = z.enum(['dev', 'privileged', 'regular']);
export const UserProfileSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    name: z.string().nullable(),
    tier: UserTierSchema,
});
//# sourceMappingURL=index.js.map